import React, { useState } from 'react'
import { loadKeys, saveKeys, clearKeys } from '../keys.js'

function Overlay({ children, onClose }) {
  return (
    <div className="overlay" onClick={onClose}>
      <div className="sheet glass" onClick={e => e.stopPropagation()}>{children}</div>
    </div>
  )
}

export function SettingsModal({ onClose, supabase, model }) {
  const [k, setK] = useState(loadKeys())
  const set = (key) => (e) => setK({ ...k, [key]: e.target.value })
  const save = () => { saveKeys(k); onClose() }
  const wipe = () => { clearKeys(); setK({}) }
  return (
    <Overlay onClose={onClose}>
      <h2>Settings & API Keys</h2>
      <div className="sub">Reasoning is powered <b>solely by {model}</b>. Asset generation uses Meshy.</div>

      <label>Claude (Anthropic) API key — powers the Opus 4.8 agents</label>
      <input className="field" type="password" value={k.anthropic || ''} onChange={set('anthropic')} placeholder="sk-ant-…" />
      <label>Meshy API key — 3D asset generation</label>
      <input className="field" type="password" value={k.meshy || ''} onChange={set('meshy')} placeholder="msy-…" />

      <div className="note" style={{ marginTop: 16 }}>
        Keys you enter here stay in <b>this browser only</b> and take priority. If you leave them blank, the deployed app uses the server's environment keys automatically. Keys are used per-request to call the provider and are never saved to any database.
      </div>
      <div className="row-actions">
        <button className="btn danger" onClick={wipe}>clear local keys</button>
        <button className="btn" onClick={onClose}>cancel</button>
        <button className="btn active" onClick={save}>save locally</button>
      </div>
    </Overlay>
  )
}

export function ChangelogModal({ onClose, state }) {
  const log = state.changelog || []
  const [showThought, setShowThought] = useState(true)
  const ind = state.projection?.indicators || {}
  const sources = Object.entries(ind).filter(([, v]) => v && typeof v === 'object' && v.url)
  return (
    <Overlay onClose={onClose}>
      <h2>Changelog · Research & Audit</h2>
      <div className="sub">Real cross-domain projections each period · every injection, asset-gen, and reasoning cycle.</div>

      <button className="btn" style={{ marginBottom: 10 }} onClick={() => setShowThought(s => !s)}>
        {showThought ? '▾ hide' : '▸ show'} agents' research & thought process
      </button>

      {showThought && (
        <div className="glass" style={{ padding: 12, marginBottom: 14, borderRadius: 10 }}>
          <div className="row" style={{ marginTop: 0 }}>Live agent reasoning ({state.agents?.[0]?.model || 'local-heuristic'})</div>
          {(state.agents || []).map(a => (
            <div key={a.window} style={{ fontSize: 11, fontVariantCaps: 'normal', padding: '6px 0', borderBottom: '1px solid var(--line)' }}>
              <b style={{ color: 'var(--accent)' }}>WINDOW {a.window}</b> · <span style={{ color: 'var(--muted)' }}>{a.worldState?.mood} · div {a.divergence}</span><br />
              <span>{a.thesis}</span>
              {a.lastCritiqueReceived && <div style={{ color: 'var(--muted)', marginTop: 3 }}>◂ critique from W{a.lastCritiqueReceived.from}: {a.lastCritiqueReceived.claim}</div>}
            </div>
          ))}
          <div className="row">Real data sources</div>
          {sources.map(([k, v]) => (
            <div key={k} style={{ fontSize: 10, fontVariantCaps: 'normal', color: 'var(--muted)' }}>
              {k}: <b style={{ color: 'var(--fg)' }}>{Math.round(v.value).toLocaleString()}</b> — {v.source}{v.live ? ' · live' : ''} {v.url && <a href={v.url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>↗</a>}
            </div>
          ))}
        </div>
      )}

      {log.length === 0 && <div className="note">No entries yet — press Play to begin the world-build.</div>}
      {log.map((e, i) => (
        <div className="logRow" key={i}>
          <span className="date">{e.simDate || `m${e.ts}`}</span>
          <span className="win">{String(e.window ?? '—')}</span>
          <span className="kind">{e.kind}</span>
          <span style={{ flex: 1 }}>{e.thesis || e.category || ''}</span>
        </div>
      ))}
      <div className="row-actions"><button className="btn active" onClick={onClose}>close</button></div>
    </Overlay>
  )
}

const PRESETS = [
  { id: 'sf', label: 'San Francisco · AI future', mode: 'sf' },
  { id: 'forest', mode: 'custom', domain: 'nature', label: 'Forest in 20 years', title: 'Forest Recovery',
    prompt: 'What would a forest look like in 20 years if we stopped cutting trees for paper and adopted a new paper resource? Research reforestation rates, canopy recovery, and alternative materials.' },
  { id: 'music', mode: 'custom', domain: 'culture', label: 'AI-only music for 50 years', title: 'AI Music Culture',
    prompt: 'What would culture look like if there were only AI-generated music for 50 years? Research effects of AI music, audio homogenization, loss of live scenes, and listener fatigue.' },
  { id: 'disease', mode: 'custom', domain: 'health', label: 'Disease & cities', title: 'Disease Trajectory',
    prompt: 'How would a major city evolve over 30 years under a recurring infectious-disease burden? Research public-health adaptation, density changes, hospital capacity, air-quality infrastructure, and population movement.' }
]

export function ResetModal({ onClose, onReset }) {
  const [prompt, setPrompt] = useState('')
  const [title, setTitle] = useState('')
  return (
    <Overlay onClose={onClose}>
      <h2>Master Controller — Reset Timeline</h2>
      <div className="sub">Reset restarts the world-build from zero and erases memory, changelog, and generated assets. The four-agent workflow is preserved. (Play/Pause does not reset.)</div>

      <label>Presets</label>
      <div>
        {PRESETS.map(p => (
          <span className="pill" key={p.id} onClick={() => { setPrompt(p.prompt || ''); setTitle(p.title || ''); if (p.mode === 'sf') onReset('sf') }}>{p.label}</span>
        ))}
      </div>

      <label style={{ marginTop: 16 }}>Custom research projection</label>
      <input className="field" value={title} onChange={e => setTitle(e.target.value)} placeholder="title (e.g. Ocean Plastic 2070)" />
      <textarea className="field" rows={3} style={{ marginTop: 8 }} value={prompt} onChange={e => setPrompt(e.target.value)}
                placeholder="Describe the world/system and time horizon to research and project…" />
      <div className="note" style={{ marginTop: 8 }}>Resets to a real-research-driven projection with maneuverable, navigable worlds and the identical four-agent DCS loop.</div>

      <div className="row-actions">
        <button className="btn" onClick={onClose}>cancel</button>
        <button className="btn" onClick={() => onReset('sf')}>reset SF demo</button>
        <button className="btn active" disabled={!prompt.trim()} onClick={() => onReset('custom', prompt, title)}>reset with research</button>
      </div>
    </Overlay>
  )
}
